import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Users } from 'lucide-react';

interface Account {
  id: string;
  email: string;
  displayName: string | null;
  emailVerifiedAt: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  stripeCustomerId: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  _count: {
    ouraImports: number;
    ouraGrokAnalyses: number;
    sessions: number;
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const statusColors: Record<string, string> = {
  ACTIVE: 'text-green-400 bg-green-400/10',
  PAST_DUE: 'text-red-400 bg-red-400/10',
  CANCELED: 'text-gray-400 bg-gray-400/10',
  INCOMPLETE: 'text-yellow-400 bg-yellow-400/10',
};

export default function HealthAccounts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const fetchAccounts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', '25');
    if (search) params.set('search', search);
    if (status) params.set('status', status);

    fetch(`/api/admin/health/accounts?${params}`)
      .then((r) => r.json())
      .then((data) => {
        setAccounts(data.accounts || []);
        setPagination(data.pagination);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading accounts:', err);
        setLoading(false);
      });
  }, [page, search, status]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) params.set('search', search);
    else params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    if (newStatus) params.set('status', newStatus);
    else params.delete('status');
    params.set('page', '1');
    setSearchParams(params);
  };

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Health Accounts</h1>
          <p className="text-gray-400 mt-1">
            {pagination ? `${pagination.total} total accounts` : 'Loading...'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, name, or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Search
          </button>
        </form>

        <div className="flex gap-2">
          {['', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'INCOMPLETE'].map((s) => (
            <button
              key={s}
              onClick={() => handleStatusFilter(s)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                status === s
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No accounts found</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Account</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Plan</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Imports</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Analyses</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Last Login</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <Link
                        to={`/health/accounts/${account.id}`}
                        className="text-primary-400 hover:text-primary-300 font-medium"
                      >
                        {account.email}
                      </Link>
                      {account.displayName && (
                        <p className="text-xs text-gray-500">{account.displayName}</p>
                      )}
                      {!account.emailVerifiedAt && (
                        <span className="text-xs text-yellow-500">Unverified</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          statusColors[account.subscriptionStatus] || ''
                        }`}
                      >
                        {account.subscriptionStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {account.subscriptionPlan || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {account._count.ouraImports}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {account._count.ouraGrokAnalyses}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {account.lastLoginAt
                        ? new Date(account.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(account.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                  className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
